import * as React from 'react';
import { ComponentProps } from 'ui';
import * as navigate from 'app/area/shared/navigation';
import { routeConstants } from 'app/routes/route-constants';
import { getWindowScroller } from 'app/infrastructure/platform/browser-dom';
import { ChildObserver, TabItem, TabContainerDomId } from 'app/components/tabs';
import { activeIdSelector } from 'app/components/tabs/visibility-observer';
import { Portal } from 'ui/common/portal';
import { scheduleUpdate } from 'app/infrastructure/platform';

/**
 * Scrolls DOM element with given id into view.
 *
 * if `forceTop` is true then a window scroll to 0,0 is performed instead of the default `el.scrollIntoView`
 *
 * @param componentId
 * @param forceTop
 */
const scrollToSection = (componentId: number | string, forceTop?: boolean) => {
  const scrollTo = getWindowScroller();
  let target: number = 0;
  const el = document.getElementById(`${componentId}`);
  if (!forceTop && el) {
    target = el.offsetTop - 120;
  }
  scrollTo(target);
};

export interface ExpectedSelectorProps extends ComponentProps {
  activeSection: number;
  firstSection: number;
  valid: boolean;
  warnings: boolean;
  expandAll: boolean;
  forceActive: boolean;
  visible: boolean;
  onSelectDocumentItem: (item: null) => void;
  hostProps: () => {
    activeChildItem: any[];
  };
  iconUrl?: any;
}

// eslint:disable:max-func-body-length
export function withSectionTab<P extends ExpectedSelectorProps>() {
  return (
    WrappedComponent: React.ComponentClass<P> | React.FC<React.PropsWithChildren<P>>
  ): React.ComponentClass<P> => {
    interface TabState {
      visibleSection?: number;
    }

    return class extends React.Component<P, TabState> {
      public constructor(props: P) {
        super(props);
        this.state = {};
      }

      public componentDidMount() {
        this.scrollIfNeeded();
      }

      public componentDidUpdate(prevProps: P) {
        if (this.props.expandAll && !prevProps.expandAll) {
          this.scrollIfNeeded();
        }
        if(prevProps.activeSection &&
            prevProps.activeSection !== this.props.activeSection){
              this.shouldActivate() && this.activate();
        }
      }

      private scrollIfNeeded() {
        this.shouldActivate() &&
          scheduleUpdate(this.scrollSectionIntoView);
      }

      private shouldActivate = () => {
        const { activeSection, componentId } = this.props;
        return activeSection && activeSection === Number(componentId);
      };

      private handleSelect = (e: React.MouseEvent<any>) => {
        e && e.preventDefault();
        this.activate();
      };

      private activate = () => {
        const { componentId } = this.props;
        navigate.toSection(componentId);
        this.scrollSectionIntoView();
      };

      private scrollSectionIntoView = () => {
        const { componentId, firstSection } = this.props;
        const shouldScrollToTop = Number(componentId) === firstSection;
        scrollToSection(componentId, shouldScrollToTop);
      };

      private handleActivate: any = activeIdSelector(
        (visibleSection: number) => {
          visibleSection &&
            visibleSection !== this.state.visibleSection &&
            this.setState({ visibleSection });
        }
      );

      public render() {
        const {
          label,
          componentId,
          activeSection,
          valid,
          warnings,
          expandAll,
          visible,
          iconUrl
        } = this.props;

        const id = Number(componentId);
        const scrollActive = expandAll && this.state.visibleSection === id;
        const tabbedActive = !expandAll && id === activeSection;
        const active = scrollActive || tabbedActive;
        const url = routeConstants.caseSection(componentId, expandAll);

        return (
          <>
            {
              <Portal forwardKey={`${id}`} toElement={TabContainerDomId}>
                <TabItem
                  key={`tabitem-${id}`}
                  id={id}
                  url={url}
                  onClick={this.handleSelect}
                  active={active}
                  label={label}
                  visible={visible}
                  valid={valid}
                  warnings={warnings}
                  iconUrl={iconUrl}
                />
              </Portal>
            }
            <ChildObserver
              name={TabContainerDomId}
              active={expandAll}
              id={id}
              onUpdate={this.handleActivate}
            >
              <WrappedComponent {...this.props} />
            </ChildObserver>
          </>
        );
      }
    };
  };
}
